/**
 * SOCRATES score-selection and presentation rules shared by the item card and
 * the Edit-item modal so the two surfaces can't drift (ARC-39).
 *
 * Two BCE passes write an UNSCORED row into the remediation log instead of
 * scoring the item: the CDE de-duplication pass (gate `cde_duplicate`,
 * sentinel cycle 99, issue `cde_duplicate_of_question_N`) and the fvg
 * parse-failure pass (gate `fvg`, cycle 5, issue `parse_failure`). Both carry
 * an empty `error_flags`, which surfaces used to render as "0/0 criteria
 * passed" — a passing grade for a record that was never scored. Those items'
 * real score, when one exists, sits in an earlier gate's row for the same
 * (session_id, question_index), so surfaces read that sibling row instead.
 */

/** One fully-attributed advisory entry from BCE's analyzer. `criterion` names
 *  the snake_case criterion key the finding belongs to (the authoritative
 *  pairing — the same keys error_flags uses), `description` the human-readable
 *  finding, and `suggestion` the actionable rewrite guidance. Historical rows
 *  may carry `criterion: "unknown"`, which surfaces in the Unattributed
 *  section rather than under a guessed criterion name. The live API delivers
 *  `suggestion: null` on the vast majority of rows, so callers must treat it
 *  as optional (ARC-302). */
export interface SocratesFeedbackEntry {
  /** BCE column letter (Q, V, Y, Z, ...) — informational only. */
  column?: string;
  severity: string;
  criterion: string;
  description: string;
  suggestion?: string | null;
}

/** Minimal structural view of a remediation-log cycle the helpers need. */
export interface SocratesCycleLike {
  cycle_number: number;
  score: number;
  passed: boolean;
  error_flags: Record<string, number>;
  /** Absent on some live Blacksmith payloads — always defaulted before use. */
  issues?: string[];
  gate_applied: string;
  /** The analyzer's per-criterion reasons for criteria it could not
   *  evaluate, keyed by the same snake_case codes as error_flags. Absent
   *  when BCE attaches no reason; surfaces fall back to the ARC-149 note. */
  not_evaluated?: Record<string, string>;
  /** Per-cycle advisory rewrites from BCE's analyzer. The live
   *  mcq_remediation_log payload places `quality_feedback` INSIDE each cycle
   *  object (alongside the error_flags/issues that cycle was scored with),
   *  not at the question level, so this must be read from the displayed
   *  cycle — see `feedbackForCycle`. Absent on question-level-era rows and
   *  on the dev sample. */
  quality_feedback?: SocratesFeedbackEntry[];
}

/**
 * The advisory array that applies to the cycle a surface is displaying.
 * BCE's live remediation payload carries `quality_feedback` per cycle (each
 * entry pairs to THAT cycle's error_flags); older stored rows, the dev
 * sample, and some historical payloads carry it at the question level.
 *
 * Field PRESENCE, not emptiness, is the discriminator: a cycle that carries
 * the key at all — including `quality_feedback: []`, meaning "this cycle has
 * no advisory to display" — is authoritative for its own flags, so a legacy
 * question-level array must NOT be attached to it. Only when the key is
 * entirely absent from the cycle object does the question-level fallback
 * apply. Display-layer concern only: nothing here touches the score,
 * criteria, or stored fields.
 */
export function feedbackForCycle(
  cycle: SocratesCycleLike | null,
  questionLevel?: ReadonlyArray<SocratesFeedbackEntry> | null,
): SocratesFeedbackEntry[] {
  if (cycle != null && "quality_feedback" in cycle) {
    return [...(cycle.quality_feedback ?? [])];
  }
  return [...(questionLevel ?? [])];
}

function hasPopulatedFlags(cycle: SocratesCycleLike): boolean {
  return !!cycle.error_flags && Object.keys(cycle.error_flags).length > 0;
}

/**
 * True for a marker-gate row that never actually evaluated any criteria — the
 * record the old "0/0" came from. A row counts as a marker when it has NO
 * populated flags and either its gate is a known unscored gate or one of its
 * issues has no rule-family prefix (cde_duplicate_of_question_N, parse_failure,
 * whatever BCE adds later). A genuinely scored cycle is never a marker, even
 * when one of its issue strings is an unprefixed annotation.
 */
export function isUnscoredMarkerCycle(cycle: SocratesCycleLike): boolean {
  if (hasPopulatedFlags(cycle)) return false;
  if (cycle.gate_applied === "cde_duplicate") return true;
  return (cycle.issues ?? []).some(isInternalPipelineMarker);
}

/**
 * For a question whose most recent row is an unscored marker, find the latest
 * earlier gate's row that actually scored the item — the "scored sibling" for
 * the same (session_id, question_index). Returns null when no scored row
 * exists (the item genuinely never was analysed; callers render the honest
 * unscored state instead of a 0/0 pass).
 */
export function resolveScoredSibling(
  cycles: SocratesCycleLike[],
): SocratesCycleLike | null {
  for (let i = cycles.length - 1; i >= 0; i--) {
    if (hasPopulatedFlags(cycles[i])) return cycles[i];
  }
  return null;
}

/**
 * The cycle a surface should render. When the most recent row is an unscored
 * marker, fall back to its scored sibling — a de-duplicated or unparseable
 * item keeps its real score, exactly as if the marker row didn't exist.
 * Otherwise render the last cycle, as before.
 */
export function effectiveCycle(
  cycles: SocratesCycleLike[],
): SocratesCycleLike | null {
  const last = cycles[cycles.length - 1];
  if (!last) return null;
  if (isUnscoredMarkerCycle(last)) return resolveScoredSibling(cycles);
  return last;
}

/**
 * Whether the resolved cycle represents a real score. Zero criteria out of
 * zero evaluated is NOT a passing grade (ARC-39): an empty flags object means
 * the item was never analysed, and surfaces must not render it as "0/0
 * criteria passed" or style it as a pass.
 */
export function isScored(cycle: SocratesCycleLike | null): boolean {
  return !!cycle && hasPopulatedFlags(cycle);
}

/**
 * True when a raw `issues` entry is an internal pipeline marker that must
 * never reach the teacher. Every genuine item-writing issue carries a
 * rule-family prefix — "V:", "T:", "Z:", "AA:", "T_conceptual:", or a
 * snake_case criterion key like "avoid_negatives:" — followed by a colon.
 * Anything else (`cde_duplicate_of_question_N`, `parse_failure`, or whatever
 * BCE adds later) has no prefix and is bookkeeping. This is a rule, not a
 * blocklist: new BCE markers are filtered with no code change.
 */
export function isInternalPipelineMarker(issue: string): boolean {
  return !/^\s*[A-Za-z][A-Za-z0-9_]*:/.test(issue);
}
